"use client";

import { useState } from "react";
import { CheckCircle2 } from "lucide-react";

interface IndustryData {
  id: string;
  name: string;
  subtitle: string;
  bullets: string[];
  handles: string[];
}

export default function IndustrySection() {
  const industries: IndustryData[] = [
    {
      id: "real-estate",
      name: "Real Estate",
      subtitle:
        "Answers property questions, checks buyer interest and books site visits.",
      bullets: [
        "Answers listing questions instantly, including price, availability, location and features.",
        "Qualifies buyers on budget, timeline and financing status, so serious buyers stand out from casual browsers.",
        "Schedules site visits and sends follow-up confirmations.",
      ],
      handles: [
        "Budget",
        "Timeline",
        "Financing status",
        "Site visit booked",
      ],
    },
    {
      id: "insurance",
      name: "Insurance",
      subtitle:
        "Checks how serious a lead is before it reaches your agents.",
      bullets: [
        "Collects coverage type, age, location and claim history.",
        "Asks about income and urgency to gauge purchase readiness.",
        "Scores intent before routing to a human agent and filters out low-intent leads automatically.",
      ],
      handles: [
        "Coverage type",
        "Age and location",
        "Claim history",
        "Income and urgency",
        "Intent score",
      ],
    },
    {
      id: "saas",
      name: "SaaS",
      subtitle:
        "Qualifies sign-ups, books demos and follows up on free trials.",
      bullets: [
        "Qualifies trial users and sign-ups with smart questions.",
        "Captures company size, budget and urgency.",
        "Scores leads and routes serious prospects to your sales team.",
        "Books demos, sends onboarding instructions and follows up with inactive users.",
      ],
      handles: [
        "Company size",
        "Budget",
        "Urgency",
        "Demo booked",
      ],
    },
    {
      id: "healthcare",
      name: "Healthcare",
      subtitle:
        "Takes over front-desk calls such as bookings and reminders.",
      bullets: [
        "Handles appointment bookings, reminders and follow-up confirmations.",
        "Automates patient communication and helps reduce no-shows.",
        "Supports clinics and hospitals with conversational call AI.",
      ],
      handles: [
        "Appointment booked",
        "Reminder sent",
        "Follow-up confirmed",
      ],
    },
    {
      id: "financial",
      name: "Financial Services",
      subtitle:
        "Screens interest and readiness before advisors get involved.",
      bullets: [
        "Evaluates income level and investment readiness.",
        "Screens clients for loans, credit cards and wealth management interest.",
        "Routes only qualified leads to advisors, cutting manual qualification in BFSI sales.",
      ],
      handles: [
        "Income level",
        "Investment readiness",
        "Loan, card or wealth interest",
        "Routed to advisor",
      ],
    },
    {
      id: "ecommerce",
      name: "E-commerce",
      subtitle:
        "Follows up after purchase and helps recover lost sales.",
      bullets: [
        "Engages customers after purchase and answers order status and issue questions in real time.",
        "Triggers follow-up offers and reminders to recover abandoned carts.",
        "Supports retention and repeat purchases.",
      ],
      handles: [
        "Order status",
        "Issue resolved",
        "Follow-up offer sent",
        "Cart recovery reminder",
      ],
    },
  ];

  const [activeTab, setActiveTab] = useState<string>("real-estate");
  const currentIndustry =
    industries.find((i) => i.id === activeTab) || industries[0];

  return (
    <section className="py-20 lg:py-24 bg-white border-b border-slate-200/80">
      <div className="w-full max-w-[1200px] mx-auto px-8 relative z-10">
        {/* Section Heading */}
        <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-10">
          Set up for the way your industry sells
        </h2>

        {/* Tab Switcher Pills */}
        <div className="flex flex-wrap gap-2.5 mb-14">
          {industries.map((ind) => {
            const isActive = ind.id === activeTab;
            return (
              <button
                key={ind.id}
                onClick={() => setActiveTab(ind.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  isActive
                    ? "bg-[#0F172A] text-white shadow-sm"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
              >
                {ind.name}
              </button>
            );
          })}
        </div>

        {/* Dynamic Content Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Copy & Bullets */}
          <div className="lg:col-span-7 space-y-5">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {currentIndustry.name}
            </h3>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              {currentIndustry.subtitle}
            </p>

            <ul className="space-y-4 pt-2">
              {currentIndustry.bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F2A339] mt-2.5 shrink-0" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column: What the agent handles Checklist Card */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-6">
                What the agent handles
              </h4>

              <div className="space-y-5">
                {currentIndustry.handles.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-slate-800">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
